<template>
    <card-grid-item
        :tab-widget="tabWidget"
        :card-class="['card-chart', 'booking-method-chart']"
        :min-w="12"
        :min-h="minHeight"
        @tab-widget-settings-click="handleTabWidgetSettingsClick"
    >
        <template v-slot:card-title>
            {{ `${title} - ${interval}` }}
        </template>
        <template v-slot:card-body>
            <busy-modal :show="busy" class="chart-widget-spinner" />
            <div v-if="ready && bookingMethodsChartData.length > 1 && interval === 'YEARLY'" class="yearly-chart">
                <vue-apex-charts height="500" :options="options" :series="series" />
            </div>
            <div v-else-if="ready && bookingMethodsChartData.length > 1 && interval !== 'YEARLY'" class="monthly-chart">
                <vue-apex-charts height="350" :options="options" :series="series" />
                <vue-apex-charts
                    :height="monthlyPeriod > 3 ? 100 : 80"
                    :options="rangeFilterOptions"
                    :series="rangeFilterSeries"
                />
                <div class="monthly-manual-select d-flex justify-content-center align-items-center">
                    <form-select
                        floatingLabel="Year"
                        class="mr-1 mr-sm-3"
                        :options="yearOptions"
                        v-model="selectedYear"
                    ></form-select>
                    <form-select
                        floatingLabel="Month"
                        class="mr-1 mr-sm-3"
                        :options="monthOptions"
                        v-model="selectedMonth"
                    ></form-select>
                    <responsive-button
                        @click="selectDateManually"
                        :icon="['fas', 'rotate']"
                        variant="primary"
                    ></responsive-button>
                </div>
            </div>
        </template>
        <template v-slot:tab-widget-settings-modal>
            <tab-widget-settings-modal-form
                :modal-id="tabWidgetSettingsModalID"
                :update="updateTabWidgetSettings"
                :initial-values="tabWidgetSettingsInitialValues"
                :with-chart-interval="true"
                :with-daily-chart-interval="false"
                :with-yearly-chart-interval="true"
                :default-interval="defaultInterval"
                @success-update="handleSuccessTabWidgetSettingsUpdate"
            ></tab-widget-settings-modal-form>
        </template>
    </card-grid-item>
</template>

<script>
import {
    format,
    getTime,
    addMonths,
    subMonths,
    subYears,
    startOfMonth,
    endOfMonth,
    isBefore,
    isAfter,
    isSameYear,
    isSameMonth,
} from 'date-fns';
import API from '../../../../api';
import BusyModal from '../../../generic/busy-modal/busy-modal.vue';
import CardGridItem from '../common/card-grid-item.vue';
import { DASHBOARD_CHART_INTERVAL_MONTHLY, DASHBOARD_CHART_INTERVAL_YEARLY } from '../../../../constants';
import { DASHBOARD_BOOKING_METHOD_BY_DATE_LIST_ENDPOINT } from '../../../../endpoints';
import TabWidgetSettingsModalForm from '../common/tab-widget-settings-modal-form.vue';
import FormSelect from '../../../generic/form-select/form-select.vue';
import ResponsiveButton from '../../../generic/responsive-button/responsive-button.vue';
import VueApexCharts from 'vue3-apexcharts';
import _ from 'lodash';
import cardGridItemBaseMixin from '../common/card-grid-item-base-mixin';

export default {
    name: 'BookingMethodChartCardGridItem',
    mixins: [cardGridItemBaseMixin],
    components: {
        BusyModal,
        CardGridItem,
        VueApexCharts,
        TabWidgetSettingsModalForm,
        FormSelect,
        ResponsiveButton,
    },
    computed: {
        yearOptions() {
            const currentYear = new Date().getFullYear();
            const currentMonth = new Date().getMonth();
            if (currentMonth === 11) {
                return [
                    { value: currentYear, text: currentYear },
                    { value: currentYear - 1, text: currentYear - 1 },
                    { value: currentYear - 2, text: currentYear - 2 },
                    { value: currentYear - 3, text: currentYear - 3 },
                    { value: currentYear - 4, text: currentYear - 4 },
                ];
            }
            return [
                { value: currentYear, text: currentYear },
                { value: currentYear - 1, text: currentYear - 1 },
                { value: currentYear - 2, text: currentYear - 2 },
                { value: currentYear - 3, text: currentYear - 3 },
                { value: currentYear - 4, text: currentYear - 4 },
                { value: currentYear - 5, text: currentYear - 5 },
            ];
        },
        monthOptions() {
            const year = this.selectedYear;
            const { minDate, maxDate } = this.getDateRangeLimit();
            const fMinDate = getTime(new Date(minDate));
            const fMaxDate = getTime(new Date(maxDate));
            const options = [
                { value: 0, text: 'January' },
                { value: 1, text: 'February' },
                { value: 2, text: 'March' },
                { value: 3, text: 'April' },
                { value: 4, text: 'May' },
                { value: 5, text: 'June' },
                { value: 6, text: 'July' },
                { value: 7, text: 'August' },
                { value: 8, text: 'September' },
                { value: 9, text: 'October' },
                { value: 10, text: 'November' },
                { value: 11, text: 'December' },
            ];
            const monthOptions = [];
            options.forEach((option) => {
                const fDate = getTime(new Date(year, option.value, 1));
                if (fDate >= fMinDate && fDate <= fMaxDate) {
                    monthOptions.push(option);
                }
            });
            return monthOptions;
        },
        tabWidgetSettingsInitialValues() {
            return {
                settings: {
                    name: this.tabWidget.settings.name || this.defaultName,
                    chart_interval: this.tabWidget.settings.chart_interval || this.defaultInterval,
                },
            };
        },
        series() {
            const seriesData = [];
            this.bookingMethods.forEach((bookingMethod, index) => {
                seriesData.push({
                    name: bookingMethod,
                    data: this.bookingMethodsChartData.map((item) => item.data[index]),
                });
            });
            return seriesData;
        },
        options() {
            return {
                chart: {
                    type: 'bar',
                    stacked: true,
                    height: this.interval !== DASHBOARD_CHART_INTERVAL_YEARLY ? 350 : 500,
                    toolbar: {
                        show: false,
                    },
                    zoom: {
                        enabled: false,
                    },
                },
                colors: this.bookingMethods.map((bm) => this.bookingMethodColors[bm]),
                annotations: {
                    points: this.bookingMethodsChartData.map((item) => {
                        return {
                            x: item.label,
                            y: item.total,
                            marker: {
                                size: 0,
                                strokeWidth: 0,
                            },
                            label: {
                                borderColor: 'transparent',
                                style: {
                                    color: '#000',
                                    background: 'transparent',
                                },
                                text: `${item.total}`,
                            },
                        };
                    }),
                },
                dataLabels: {
                    enabled: true,
                    formatter: (val, { dataPointIndex, w }) => {
                        const total = w.config.series.reduce(
                            (prev, current) => prev + (current.data[dataPointIndex] || 0),
                            0
                        );
                        if (val === 0 || total === 0) {
                            return `0.00%`;
                        } else {
                            return `${((val / total) * 100).toFixed(2)}%`;
                        }
                    },
                    style: {
                        fontSize: '10px',
                    },
                },
                tooltip: {
                    custom: (params) => {
                        const { series, seriesIndex, dataPointIndex, w } = params;
                        const total = w.config.series.reduce(
                            (prev, current) => prev + (current.data[dataPointIndex] || 0),
                            0
                        );
                        const val = series[seriesIndex][dataPointIndex];
                        let content = '';
                        if (val === 0 || total === 0) {
                            content = `${this.bookingMethods[seriesIndex]}: 0.00% (0)`;
                        } else {
                            content = `${this.bookingMethods[seriesIndex]}: ${((val / total) * 100).toFixed(
                                2
                            )}% (${val})`;
                        }
                        return (
                            '<div class="booking-methods-widget-tooltip">' + '<span>' + content + '</span>' + '</div>'
                        );
                    },
                },
                xaxis: {
                    categories: this.bookingMethodsChartData.map((item) => item.label),
                },
                yaxis: {
                    show: false,
                },
                legend: {
                    onItemClick: {
                        toggleDataSeries: false,
                    },
                },
                plotOptions: {
                    bar: {
                        columnWidth: this.monthlyPeriod === 3 ? '90%' : this.monthlyPeriod === 6 ? '80%' : '70%',
                    },
                },
            };
        },
        rangeFilterSeries() {
            return [
                {
                    name: 'monthlyRangeFilter',
                    data: this.chartData.map((item) => item.total),
                },
            ];
        },
        rangeFilterOptions() {
            let fromDate = new Date(this.selectedDate.getFullYear(), this.selectedDate.getMonth(), 1);
            const maxDate = endOfMonth(new Date());
            let toDate = endOfMonth(
                addMonths(
                    new Date(this.selectedDate.getFullYear(), this.selectedDate.getMonth(), 1),
                    this.monthlyPeriod - 1
                )
            );

            if (isAfter(toDate, maxDate)) {
                toDate = new Date(maxDate);
                fromDate = startOfMonth(subMonths(new Date(maxDate), this.monthlyPeriod - 1));
            }

            return {
                chart: {
                    type: 'area',
                    height: this.monthlyPeriod > 3 ? 100 : 80,
                    toolbar: {
                        show: false,
                        autoSelected: 'selection',
                    },
                    selection: {
                        enabled: true,
                        xaxis: {
                            min: new Date(fromDate).getTime(),
                            max: new Date(toDate).getTime(),
                        },
                    },
                    events: {
                        selection: (ctx, { xaxis }) => {
                            const { max } = xaxis;
                            if (
                                isSameYear(new Date(max), new Date(this.maxDate)) &&
                                isSameMonth(new Date(max), new Date(this.maxDate))
                            ) {
                                this.monthlyRange = {
                                    min: endOfMonth(subMonths(new Date(max), this.monthlyPeriod - 1)),
                                    max: endOfMonth(new Date(max)),
                                };
                            } else {
                                this.monthlyRange = { ...xaxis };
                            }
                        },
                    },
                },
                dataLabels: {
                    enabled: false,
                },
                xaxis: {
                    type: 'datetime',
                    categories: this.chartData.map((item) => item.date),
                    labels: {
                        format: 'yyyy MMM',
                    },
                    tooltip: {
                        enabled: false,
                    },
                    min: new Date(this.minDate).getTime(),
                    max: new Date(this.maxDate).getTime(),
                },
                yaxis: {
                    show: false,
                },
                tooltip: {
                    enabled: false,
                },
                legend: {
                    show: false,
                },
            };
        },
    },
    created() {
        this.debounceHandleDateRangeChange = _.debounce(this.prepareChartData, 1000);
    },
    async mounted() {
        this.interval = this.tabWidget.settings.chart_interval || this.defaultInterval;
        this.onResize();
        window.addEventListener('resize', this.onResize);
        await this.fetchBookingMethodData();
        this.prepareChartData();
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.onResize);
    },
    data() {
        return {
            busy: false,
            chartData: [],
            bookingMethods: [],
            bookingMethodColors: {},
            bookingMethodsChartData: [],
            interval: DASHBOARD_CHART_INTERVAL_YEARLY,
            defaultInterval: DASHBOARD_CHART_INTERVAL_YEARLY,
            selectedYear: new Date().getFullYear(),
            selectedMonth: new Date().getMonth(),
            selectedDate: new Date(),
            yearlyPeriod: 5,
            monthlyPeriod: 12,
            monthlyRange: {
                min: new Date(),
                max: new Date(),
            },
            minDate: null,
            maxDate: null,
        };
    },
    methods: {
        onResize() {
            if (window.innerWidth > 991) {
                this.monthlyPeriod = 12;
            } else if (window.innerWidth > 767 && window.innerWidth < 992) {
                this.monthlyPeriod = 6;
            } else {
                this.monthlyPeriod = 3;
            }
        },
        getDateRangeLimit() {
            const minDate = startOfMonth(addMonths(subYears(new Date(), 5), 1));
            const maxDate = endOfMonth(new Date());
            return {
                minDate,
                maxDate,
            };
        },
        convertTruncDate(dateString) {
            const year = Number(dateString.split('-')[0]);
            const month = Number(dateString.split('-')[1]) - 1;
            const day = Number(dateString.split('-')[2]);
            return new Date(year, month, day);
        },
        async fetchBookingMethodData() {
            const { minDate, maxDate } = this.getDateRangeLimit();
            this.minDate = minDate;
            this.maxDate = maxDate;
            try {
                this.busy = true;
                let bmData = [];
                if (this.interval !== DASHBOARD_CHART_INTERVAL_YEARLY) {
                    const { data } = await API.get(DASHBOARD_BOOKING_METHOD_BY_DATE_LIST_ENDPOINT(), {
                        params: {
                            interval: DASHBOARD_CHART_INTERVAL_MONTHLY,
                            period: 60,
                        },
                    });
                    bmData = [...data];
                } else {
                    const { data } = await API.get(DASHBOARD_BOOKING_METHOD_BY_DATE_LIST_ENDPOINT(), {
                        params: {
                            interval: DASHBOARD_CHART_INTERVAL_YEARLY,
                            period: new Date().getMonth() < 11 ? 6 : 5,
                        },
                    });
                    bmData = [...data];
                }
                this.bookingMethodColors = {};
                const data = bmData.map((item) => {
                    const { trunc_date, booking_method: bm, id__count: count, chart_color: color } = item;
                    if (!this.bookingMethodColors[bm]) {
                        this.bookingMethodColors[bm] = color;
                    }
                    return {
                        count,
                        bm,
                        date:
                            this.interval !== DASHBOARD_CHART_INTERVAL_YEARLY
                                ? this.convertTruncDate(trunc_date)
                                : trunc_date.substring(0, 4),
                        color,
                    };
                });

                this.chartData = [];
                if (this.interval !== DASHBOARD_CHART_INTERVAL_YEARLY) {
                    let counter = 0;
                    while (counter < 60) {
                        const startDate = startOfMonth(addMonths(new Date(this.minDate), counter));
                        const endDate = endOfMonth(addMonths(new Date(this.minDate), counter));

                        const filteredData = data.filter((item) => {
                            const date = new Date(item.date);
                            return !(isBefore(date, startDate) || isAfter(date, endDate));
                        });

                        if (filteredData.length === 0) {
                            this.chartData.push({ date: startDate, total: 0, data: [] });
                        } else {
                            const item = { date: startDate, total: 0, data: [] };
                            filteredData.forEach((d) => {
                                const { count, bm } = d;
                                item.total += count;
                                item.data.push({ bm, count });
                            });
                            this.chartData.push(item);
                        }

                        counter += 1;
                    }
                } else {
                    const currentMonth = new Date().getMonth();
                    const currentYear = new Date().getFullYear();
                    let yearCounter = currentMonth < 11 ? currentYear - 5 : currentYear - 4;
                    while (yearCounter <= currentYear) {
                        const filteredData = data.filter((item) => item.date === `${yearCounter}`);

                        if (filteredData.length === 0) {
                            this.chartData.push({ date: yearCounter, total: 0, data: [] });
                        } else {
                            const item = { date: yearCounter, total: 0, data: [] };
                            filteredData.forEach((d) => {
                                const { count, bm } = d;
                                item.total += count;
                                item.data.push({ bm, count });
                            });
                            this.chartData.push(item);
                        }

                        yearCounter += 1;
                    }
                }
            } catch {
                this.errorToast({ text: 'Could not load booking methods data.' });
            } finally {
                this.busy = false;
            }
        },
        selectDateManually() {
            this.selectedDate = new Date(this.selectedYear, this.selectedMonth, 1);
        },
        prepareChartData() {
            if (this.interval === DASHBOARD_CHART_INTERVAL_MONTHLY) {
                const { min } = this.monthlyRange;
                const minDate = startOfMonth(new Date(min));
                const maxDate = endOfMonth(addMonths(new Date(min), this.monthlyPeriod - 1));

                this.bookingMethods = [];
                this.chartData
                    .filter((item) => {
                        const date = new Date(item.date);
                        return !(isBefore(date, minDate) || isAfter(date, maxDate));
                    })
                    .forEach((item) => {
                        const bmList = item.data.map((d) => d.bm);
                        bmList.forEach((bm) => {
                            if (!this.bookingMethods.includes(bm)) {
                                this.bookingMethods.push(bm);
                            }
                        });
                    });

                this.bookingMethodsChartData = [];
                let counter = 0;
                while (counter < this.monthlyPeriod) {
                    const chartData = { label: '', total: 0, data: [] };
                    const fromDate = startOfMonth(addMonths(new Date(minDate), counter));
                    const toDate = endOfMonth(addMonths(new Date(minDate), counter));
                    chartData.label = format(fromDate, 'yyyy MMM');
                    chartData.data = [...this.bookingMethods.map((_) => 0)];
                    this.chartData
                        .filter((item) => {
                            const date = new Date(item.date);
                            return !(isBefore(date, fromDate) || isAfter(date, toDate));
                        })
                        .forEach((item) => {
                            const { total, data } = item;
                            chartData.total += total;
                            this.bookingMethods.forEach((bm, index) => {
                                data.forEach((d) => {
                                    if (d.bm === bm) {
                                        chartData.data[index] += d.count;
                                    }
                                });
                            });
                        });
                    this.bookingMethodsChartData.push(chartData);
                    counter += 1;
                }
            } else {
                this.bookingMethods = [];
                this.chartData.forEach((item) => {
                    const bmList = item.data.map((d) => d.bm);
                    bmList.forEach((bm) => {
                        if (!this.bookingMethods.includes(bm)) {
                            this.bookingMethods.push(bm);
                        }
                    });
                });
                this.bookingMethodsChartData = [];

                this.chartData.forEach((item) => {
                    const chartData = { label: `${item.date}`, total: item.total, data: [] };
                    this.bookingMethods.forEach((_) => {
                        chartData.data.push(0);
                    });
                    this.bookingMethods.forEach((bm, index) => {
                        item.data.forEach((d) => {
                            if (d.bm === bm) {
                                chartData.data[index] += d.count;
                            }
                        });
                    });
                    this.bookingMethodsChartData.push(chartData);
                });
            }
        },
    },
    watch: {
        monthlyRange: {
            handler(dateRange) {
                const { min } = dateRange;
                this.debounceHandleDateRangeChange();
                this.selectedYear = new Date(min).getFullYear();
                this.selectedMonth = new Date(min).getMonth();
            },
            deep: true,
        },
    },
};
</script>

<style lang="scss">
.booking-method-chart {
    .svg_select_points {
        display: none !important;
    }
    .monthly-manual-select {
        max-height: 40px;
        fieldset {
            margin-bottom: 0;
        }
    }
}
.booking-methods-widget-tooltip {
    padding: 4px;
    font-size: 10px;
    font-weight: bold;
    line-height: 1;
    color: #373d3f;
    > span {
        padding: 0;
    }
}
</style>